// Optum Now partner flow: Impact links land on `/us/ring-pro/buy?flow=optum`;
// checkout is the Shopify cart with the Optum discount (resolved server-side).
// See docs/optum-flow.md.

/** `?flow=optum` on a US Optum page is the only switch for Optum mode; links must keep it. */
export const OPTUM_FLOW_PARAM = 'flow';
export const OPTUM_FLOW_VALUE = 'optum';

/** Optum buy page; the header logo links here. CustomLink adds the region prefix. */
export const OPTUM_BUY_PATH = '/ring-pro/buy';
/** Internal (region-stripped) paths that render in Optum mode with `?flow=optum`. */
const OPTUM_INTERNAL_PATHS = [OPTUM_BUY_PATH];

/**
 * Public stand-in the browser sends for the Optum discount. `/api/cart` swaps it for the
 * real Shopify code and masks the code back in responses (lib/partner/optumDiscount.server.ts).
 */
export const OPTUM_DISCOUNT_ALIAS = 'OPTUM-NOW';

/**
 * A search param as Next gives it on the server (`string` or `string[]` when the
 * key repeats) or as `URLSearchParams.get` returns it on the client.
 */
export type QueryParamValue = string | readonly string[] | null | undefined;

/**
 * First value of `?flow=`. A repeated key matches `URLSearchParams.get` (the first
 * value), so server metadata and client chrome cannot disagree.
 */
export function readFlowParam(flow: QueryParamValue): string | null {
  if (typeof flow === 'string') return flow;
  if (flow == null) return null;
  return flow[0] ?? null;
}

/** What decides partner mode: the visitor's region, the region-stripped path and `?flow=`. */
export type PartnerFlowInput = {
  region: string | null | undefined;
  internalPath: string;
  flow: QueryParamValue;
};

/** The one test for Optum mode, shared by the buy page, PartnerModeContext and the cart. */
export function isOptumFlow({
  region,
  internalPath,
  flow,
}: PartnerFlowInput): boolean {
  const normalized = internalPath.replace(/\/+$/, '') || '/';
  return (
    region?.toUpperCase() === 'US' &&
    OPTUM_INTERNAL_PATHS.includes(normalized) &&
    readFlowParam(flow) === OPTUM_FLOW_VALUE
  );
}

/** Discount codes with the Optum alias taken out; off the flow it must leave the cart. */
export function withoutOptumAlias(codes: ReadonlyArray<string>): string[] {
  return codes.filter((code) => code !== OPTUM_DISCOUNT_ALIAS);
}
